import { FC } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from 'lib/utils';
import { SelectPart, selectStates } from './select';
import { uiContainer, uiInput } from '../CutCorners';

type SelectItem = {
  value: string;
  label: string;
};

interface BPSelectProps {
  options: SelectItem[];
  state?: keyof typeof selectStates;
  twWidth?: string | null;
  placeholder: string;
  defaultValue?: string;
  disabled?: boolean;
  useContainerEdge?: boolean;
  onValueChange?: (value: string) => void;
}

const BPSelect: FC<BPSelectProps> = ({
  state = 'default',
  options,
  twWidth = null,
  placeholder = '',
  defaultValue,
  disabled = false,
  useContainerEdge = false,
  onValueChange = () => {},
}) => {
  return (
    <div
      className={cn(
        'inline-block relative h-[72px] pr-[8px] pl-[20px]',
        twWidth ?? 'w-full sm:w-[446px]'
      )}
    >
      <Select
        disabled={disabled}
        onValueChange={onValueChange}
        defaultValue={defaultValue}
      >
        <SelectTrigger className="group/input grid gap-4 items-center relative z-10">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white/[0.05] backdrop-blur-md border-none rounded-2xl">
          {options.map((option) => (
            <SelectItem
              className="rounded-none text-white text-[16px] font-medium"
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
        <Background state={state} useContainerEdge={useContainerEdge} />
        <Glow state={state} useContainerEdge={useContainerEdge} />
      </Select>
    </div>
  );
};

const Background: FC<{
  state: keyof typeof selectStates;
  useContainerEdge?: boolean;
}> = ({ state, useContainerEdge = false }) => {
  const base =
    'block absolute top-0 left-0 w-full h-full pointer-events-none z-0';

  const getPartClasses = (part: SelectPart): string =>
    [part.default, part.hover, part.focus, part.disabled].join(' ');

  const bg = getPartClasses(selectStates[state].bg);
  const border = getPartClasses(selectStates[state].border);

  return (
    <>
      <div
        className={cn(
          base,
          bg,
          'backdrop-blur-md',
          useContainerEdge ? uiContainer.bg : uiInput.bg
        )}
      />
      <div
        className={cn(
          base,
          border,
          useContainerEdge ? uiContainer.border : uiInput.border
        )}
      />
    </>
  );
};

const Glow: FC<{
  state: keyof typeof selectStates;
  useContainerEdge?: boolean;
}> = ({ state, useContainerEdge = false }) => (
  <div
    className={cn(
      'grid absolute z-10 top-0 left-0 w-[200px] h-full items-center justify-start pointer-events-none peer-disabled/field:hidden',
      useContainerEdge ? uiContainer.bg : uiInput.bg
    )}
  >
    <div
      className={cn(
        'block absolute -left-[40px] w-[48px] h-[40px] blur-md transition-all duration-500 ease-in',
        selectStates[state].glow
      )}
    />
  </div>
);

export default BPSelect;
